// Global
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import {
  withDatasourceCheck,
  Text,
  GetStaticComponentProps,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

// Components
import { ReviewSummaryTheme } from './ReviewSummary.theme';
import { StarRating } from 'src/helpers/StarRating';
import { ProgressBar } from 'src/helpers/ProgressBar';

// Lib
import {
  parseReviewFilterData,
  getReviewsSummary,
  createReviewSummaryFilter,
} from 'lib/reviews/get-reviews-v2';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import { ReviewsList } from '../../../helpers/ReviewsList';
import Component from 'src/helpers/Component/Component';
import { Fragment } from 'react';
import { conditionParametersFactory } from 'lib/rules/parameters/condition-parameters';
import { FilterRule, ReviewSummaryStaticProps } from 'lib/reviews/types';
import { applySuperscripts } from 'src/helpers/Superscripts/superscript';

export type ReviewSummaryProps =
  Feature.EnterpriseWeb.RenewalByAndersen.Components.Reviews.ReviewSummaryAndList;
const ReviewSummary = (props: ReviewSummaryProps) => {
  const { themeData } = useTheme(ReviewSummaryTheme);
  const { currentScreenWidth } = useCurrentScreenType();
  const isMobile = currentScreenWidth < getBreakpoint('md');
  const isTablet =
    currentScreenWidth >= getBreakpoint('md') && currentScreenWidth < getBreakpoint('lg');
  const starSize = isMobile ? '22' : isTablet ? '28' : '32';
  const listIconSize = isMobile ? '20' : isTablet ? '22' : '25';

  const averageRating = props.productReview?.averageRating ?? 0;
  const totalReviews = props.productReview?.totalReviews ?? 0;
  const showReviewList = props.fields?.showReviewList?.value || false;

  const raw = props?.fields?.title?.value ?? '';
  // If this Sitecore field is plain text (single/multi-line/RTE):
  const processedTitleField = applySuperscripts(raw, { isRichText: false });

  const ratingStars = (isMobileView: boolean) => {
    const sortedRatings = props.productReview?.starRatings.sort(
      (a: { rating: number }, b: { rating: number }) => b.rating - a.rating
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return sortedRatings?.map((starRating: any) => {
      const isComplete = starRating?.numberOfRatings === totalReviews;

      return (
        <Fragment key={starRating.rating}>
          {isMobileView ? (
            <div
              id={starRating?.rating}
              className={classNames(
                'items-center justify-items-center px-xxs py-[2px]',
                themeData.classes.ratingsIconsList
              )}
              data-rating={starRating?.rating}
            >
              {ratingStarItem(starRating, isComplete, listIconSize)}
            </div>
          ) : (
            <div
              className={classNames(themeData.classes.ratingsIconsList, 'items-center')}
              data-rating={starRating?.rating}
              tabIndex={0}
              id={'starRating' + starRating?.rating}
            >
              {ratingStarItem(starRating, isComplete, listIconSize)}
            </div>
          )}
        </Fragment>
      );
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ratingStarItem = (starRating: any, isComplete: boolean, iconSize: string) => {
    return (
      <>
        <div>
          <StarRating
            reviewStars={starRating?.rating}
            iconSize={iconSize}
            containerCSSClass="ratingStars"
          />
        </div>
        <div className={themeData.classes.progressBarWrapper}>
          <ProgressBar
            percent={(Number(starRating?.numberOfRatings + 1) / (Number(totalReviews) + 1)) * 100}
            stepLabel={''}
            isComplete={isComplete}
            activeStep={starRating?.numberOfRatings + 1}
            totalSteps={Number(totalReviews) + 1}
            showSteps={false}
          />
        </div>
        <div className={classNames(themeData.classes.totalReviewsText)}>
          <Text field={{ value: starRating?.numberOfRatings.toLocaleString() }} />
        </div>
      </>
    );
  };

  const toggleDropdown = () => {
    const dropdown = document.querySelector('#dropdown');
    dropdown?.classList.toggle('hidden');
    const dropdownBtn = document.querySelector('#dropdownButton');
    dropdownBtn?.classList.toggle('rotate-180');
  };

  return (
    <Component variant="lg" dataComponent="reviews/reviewsummary" {...props}>
      {props?.productReview ? (
        <div className="col-span-12">
          <div className={classNames(themeData.classes?.summaryTitle, 'read-aloud')}>
            <span dangerouslySetInnerHTML={{ __html: processedTitleField }} />
          </div>
          <div className="mb-s flex flex-col sm:ml-[4px] lg:mb-m lg:flex-row lg:items-center lg:gap-4 lg:pl-s">
            <div
              className={classNames(themeData.classes.ratingsAndReviewCountWrapper, 'read-aloud')}
            >
              <div className={themeData.classes.ratingText}>
                <Text
                  className="md:text-xl"
                  field={{
                    editable: averageRating.toFixed(1),
                    value: averageRating.toFixed(1),
                  }}
                />
              </div>
              <div className="flex flex-row gap-1 md:flex-col">
                <div>
                  <StarRating
                    reviewStars={averageRating}
                    iconSize={starSize}
                    containerCSSClass="ratingStars"
                  />
                </div>

                <div className={classNames(themeData.classes.totalReviewsText)}>
                  <span>
                    out of&nbsp;
                    <Text field={{ value: totalReviews.toLocaleString() }} />
                    &nbsp;reviews
                  </span>
                </div>
              </div>
              {currentScreenWidth < getBreakpoint('md') && (
                <div id="dropdownButton" className="mt-2" onClick={() => toggleDropdown()}>
                  <SvgIcon icon="dropdown-arrow" size="lg" />
                </div>
              )}
            </div>

            <div className={themeData.classes?.ratingsWrapper} id="dropdown">
              {currentScreenWidth < getBreakpoint('md') ? ratingStars(true) : ratingStars(false)}
            </div>
          </div>

          {showReviewList && (
            <div>
              <ReviewsList {...props} />
            </div>
          )}
        </div>
      ) : (
        <></>
      )}
    </Component>
  );
};

/* istanbul ignore next - We aren't running E2E tests. */
export const getStaticProps: GetStaticComponentProps = async (rendering, layout, context) => {
  const props: ReviewSummaryStaticProps = {};
  const datasource = rendering as ReviewSummaryProps;

  if (!datasource) {
    return {};
  }

  const rules = await conditionParametersFactory.extend(
    datasource.fields?.reviewFilters?.value,
    context,
    layout
  );
  const filters = parseReviewFilterData({
    reviewFilterProps: {
      ...datasource,
    },
    reviewFilters: rules,
  });

  const reviewSummary = await getReviewsSummary(
    createReviewSummaryFilter(filters.filterRules as FilterRule)
  );

  props.productReview = reviewSummary ?? undefined;
  props.reviewsFilterRules = rules;

  return props;
};

export default withDatasourceCheck()<ReviewSummaryProps>(ReviewSummary);
